/**
 * Firebase Authentication Service
 *
 * Provides authentication methods for the DARAK web application.
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updateProfile,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { getFirebaseErrorMessage } from '../utils/errors';
import type { User, UserRole } from '@/types/shared';

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

export interface AuthError {
  code: string;
  message: string;
}

/**
 * Sign in with email and password
 */
export async function signIn(
  email: string,
  password: string
): Promise<{ user: FirebaseUser; profile: User | null }> {
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const profile = await getUserProfile(credential.user.uid);
    return { user: credential.user, profile };
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Sign up with email and password
 * Creates both Firebase Auth user and Firestore user document
 */
export async function signUp(data: SignUpData): Promise<{ user: FirebaseUser; profile: User }> {
  try {
    // Create Firebase Auth user
    const credential = await createUserWithEmailAndPassword(auth, data.email, data.password);

    // Update display name
    const displayName = `${data.firstName} ${data.lastName}`;
    await updateProfile(credential.user, { displayName });

    // Create Firestore user document
    const profile: Omit<User, 'id'> = {
      email: data.email,
      displayName,
      firstName: data.firstName,
      lastName: data.lastName,
      role: 'guest' as UserRole,
      phone: data.phone,
      createdAt: new Date(),
      updatedAt: new Date(),
      isVerified: false,
      isSuspended: false,
    };

    await setDoc(doc(db, 'users', credential.user.uid), {
      ...profile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      user: credential.user,
      profile: { id: credential.user.uid, ...profile },
    };
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Google sign-in.
 *
 * Uses a popup, not a redirect. `authDomain` is the project's
 * firebaseapp.com domain (it has to be — that is the only origin registered as
 * an authorized redirect URI on the OAuth client), while the app is served from
 * darak.online. `signInWithRedirect` cannot complete across those two origins
 * on browsers that partition or block third-party storage (Safari, Firefox,
 * Chrome with third-party cookies restricted): Google redirects back to
 * `https://<authDomain>/__/auth/handler`, which then has no access to the
 * app-origin storage holding the pending-redirect state, so the user lands back
 * on the site silently signed out.
 *
 * `signInWithPopup` has no such constraint — the popup posts the credential
 * back to the opener directly. Cross-Origin-Opener-Policy is already
 * `same-origin-allow-popups` in firebase.json, which keeps that channel open.
 *
 * If the popup is blocked (or unsupported, e.g. an in-app webview), fall back
 * to the redirect flow so the user at least gets a chance rather than nothing.
 */
export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  try {
    await signInWithPopup(auth, provider);
  } catch (error: unknown) {
    const code = (error as { code?: string }).code;
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      try {
        await signInWithRedirect(auth, provider);
        return;
      } catch (redirectError: unknown) {
        throw createAuthError(redirectError);
      }
    }
    // The user closing the popup is not an error worth surfacing loudly, but
    // callers still need to stop their spinner, so it is still thrown.
    throw createAuthError(error);
  }
}

/**
 * Get the result of a Google sign-in redirect
 * Call this on page load to check if user is returning from Google auth
 */
export async function getGoogleRedirectResult(): Promise<UserCredential | null> {
  try {
    return await getRedirectResult(auth);
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Sign out current user
 */
export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth);
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChanged(
  callback: (user: FirebaseUser | null) => void
): () => void {
  return firebaseOnAuthStateChanged(auth, callback);
}

/**
 * Get current user
 */
export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser;
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<User | null> {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as User;
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<User, 'id' | 'email' | 'role' | 'createdAt' | 'isSuspended'>>
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);
    await setDoc(
      docRef,
      {
        ...updates,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // Update Firebase Auth display name if provided
    if (updates.displayName && auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName: updates.displayName });
    }

    // Update Firebase Auth photo URL if provided
    if (updates.photoURL && auth.currentUser) {
      await updateProfile(auth.currentUser, { photoURL: updates.photoURL });
    }
  } catch (error: unknown) {
    throw createAuthError(error);
  }
}

/**
 * Create standardized auth error
 */
function createAuthError(error: unknown): AuthError {
  const firebaseError = error as { code?: string; message?: string };
  return {
    code: firebaseError.code || 'unknown',
    message: getFirebaseErrorMessage(firebaseError.code || 'unknown'),
  };
}
